import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/Common";
import api from "@/lib/api";

const PERIODS = [
  { k: "daily", l: "Analisi Giornaliera" },
  { k: "weekly", l: "Analisi Settimanale" },
  { k: "monthly", l: "Analisi Mensile" },
];

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    // Uppercase heading heuristic
    const isHeading = t.length < 60 && t === t.toUpperCase() && /[A-Z]/.test(t) && !t.startsWith("-");
    if (isHeading) return <h3 key={i} className="mt-5 font-sans text-sm font-semibold uppercase tracking-widest text-gold">{t.replace(/[#*]/g, "")}</h3>;
    const clean = t.replace(/^[-*•]\s?/, "").replace(/\*\*/g, "");
    if (t.startsWith("-") || t.startsWith("•") || t.startsWith("*"))
      return <div key={i} className="ml-1 flex gap-2 font-sans text-sm text-zinc-300"><span className="text-gold">·</span><span>{clean}</span></div>;
    return <p key={i} className="font-sans text-sm leading-relaxed text-zinc-300">{clean}</p>;
  });
}

export default function AIAnalyst() {
  const [period, setPeriod] = useState("daily");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (p) => {
    setPeriod(p); setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/ai/analyze", { period: p });
      setResult(data);
    } catch (e) {
      setError("Errore durante l'analisi AI. Riprova.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="ai-analyst-page">
      <PageHeader title="DELUXE AI Analyst" subtitle="Analisi storica dei tuoi dati · Claude Sonnet 4.6" />

      <div className="dlx-card dlx-grain mb-6 p-6">
        <div className="flex items-start gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-xl blur-lg" style={{ background: "rgba(212,175,55,0.25)" }} />
            <div className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-[#141418]">
              <Sparkles size={20} className="text-gold" />
            </div>
          </div>
          <div className="flex-1">
            <p className="font-sans text-sm text-zinc-400">
              L'AI analizza esclusivamente i tuoi dati storici. Nessun segnale di trading, nessuna previsione futura — solo osservazioni concrete su ciò che è già accaduto.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {PERIODS.map((p) => (
                <button key={p.k} onClick={() => run(p.k)} disabled={loading}
                  data-testid={`ai-analyst-generate-${p.k}`}
                  className={`rounded-lg border px-4 py-2 font-sans text-xs font-medium transition-all disabled:opacity-50 ${
                    period === p.k && (loading || result)
                      ? "border-gold/50 bg-gold/10 text-gold"
                      : "border-white/10 text-zinc-300 hover:border-gold/40 hover:text-white"}`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="dlx-card flex min-h-[300px] flex-col items-center justify-center gap-4 p-8" data-testid="ai-loading">
          <Loader2 size={28} className="animate-spin text-gold" />
          <span className="font-mono text-xs tracking-widest text-zinc-500">L'AI sta analizzando i tuoi dati…</span>
        </div>
      )}

      {error && <div className="dlx-card border-loss/30 p-6 font-sans text-sm text-loss">{error}</div>}

      {result && !loading && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="dlx-card p-6 sm:p-8" data-testid="ai-result">
          <div className="mb-5 flex items-center justify-between border-b border-white/8 pb-4">
            <span className="font-serif text-xl text-white">{PERIODS.find((p) => p.k === result.period)?.l}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{result.trades_analyzed} trade analizzati</span>
          </div>
          <div className="space-y-1">{renderText(result.analysis)}</div>
        </motion.div>
      )}
    </div>
  );
}
